import { SelectV2, SelectV2Option } from '@cimplico/components';
import { useEffect, useMemo, useState } from 'react';
import { useEntityTaxRates } from '../../queries/use-entity-tax-rates';
import { AccountClassification } from '../../types/entity';

type TaxRateSelectProps = {
    entityId: string;
    accountClass?: AccountClassification;
    value?: string | null;
    errorMessage?: string;
    disabledHint?: string;
    isDisabled?: boolean;
    onChange: (taxRate: { id?: string; name?: string; rate?: number } | null) => void;
};

function TaxRateSelect({
    entityId,
    accountClass,
    value,
    errorMessage,
    disabledHint,
    isDisabled,
    onChange,
}: TaxRateSelectProps): JSX.Element {
    const [selected, setSelected] = useState<SelectV2Option[] | undefined>(undefined);
    const [search, setSearch] = useState('');

    const taxRatesQuery = useEntityTaxRates(entityId);

    const allOptions: SelectV2Option[] = useMemo(() => {
        const allOptions: SelectV2Option[] = (taxRatesQuery.data?.entityTaxRates || [])
            .filter((rate) => rate.name.toString().toLowerCase().includes(search.toLowerCase()))
            .filter((opt) => (accountClass ? opt.type.includes(accountClass) : opt))
            .map((option) => ({
                value: option.id,
                label: `${option.name} (${option.rate}%)`,
            }));
        return allOptions;
    }, [taxRatesQuery.data?.entityTaxRates, search, accountClass]);

    function handleTaxRateChange(options: SelectV2Option[]): void {
        onChange(
            options.length === 0
                ? null
                : {
                      id: options[0].value as string,
                      name: taxRatesQuery.data?.entityTaxRates.find((r) => r.id === options[0].value)?.name,
                      rate: taxRatesQuery.data?.entityTaxRates.find((r) => r.id === options[0].value)?.rate,
                  }
        );
    }

    useEffect(() => {
        if (value && taxRatesQuery.data?.entityTaxRates) {
            const matchingRate = taxRatesQuery.data?.entityTaxRates.find((r) => r.id === value);
            const defaultSelected: SelectV2Option[] = [
                { label: matchingRate ? `${matchingRate.name} (${matchingRate.rate}%)` : '', value: value },
            ];
            setSelected(defaultSelected);
        } else if (!value) {
            setSelected(undefined);
        }
    }, [value, taxRatesQuery.data?.entityTaxRates]);

    useEffect(() => {
        if (!accountClass) {
            setSelected(undefined);
            onChange(null);
        } else if (accountClass && allOptions.length > 0) {
            if (taxRatesQuery.data?.entityTaxRates) {
                if (value || selected?.length === 1) {
                    const selectedOption = taxRatesQuery.data?.entityTaxRates.find(
                        (r) => r.id === (selected?.length === 1 ? selected[0].value : value)
                    );
                    if ((selectedOption?.type || []).includes(accountClass)) {
                        return;
                    }
                }
                onChange({
                    id: allOptions[0].value as string,
                    name: taxRatesQuery.data?.entityTaxRates.find((r) => r.id === allOptions[0].value)?.name,
                    rate: taxRatesQuery.data?.entityTaxRates.find((r) => r.id === allOptions[0].value)?.rate,
                });
            }
        }
    }, [accountClass]);

    return (
        <div>
            <SelectV2
                size="small"
                placeholder="Tax rate..."
                keepSelectedOptions
                borderStyle="underline"
                onSearch={setSearch}
                errorMessage={errorMessage}
                value={selected}
                disabled={isDisabled || !accountClass}
                disabledHint={disabledHint || (!accountClass ? 'An account must be selected to select a tax rate.' : undefined)}
                onChange={handleTaxRateChange}
                options={allOptions}
                allowOverflow
            />
        </div>
    );
}
export default TaxRateSelect;
